import { RingBuffer } from './ring-buffer';
import type { ActivityEntry, UiActivity } from './types';

/** Entries kept: well over a minute of busy use, and a snapshot takes far fewer. */
export const DEFAULT_CAPACITY = 200;

/** Entries older than this when a snapshot is taken are not part of it. */
export const DEFAULT_MAX_AGE_MS = 60_000;

export interface ActivityLogOptions {
  capacity?: number;
  maxAgeMs?: number;
}

export interface ActivityLog {
  record(entry: ActivityEntry): void;
  /** The entries of the last `maxAgeMs` before `now`, oldest first. */
  recent(now?: number): ActivityEntry[];
  clear(): void;
}

/** Events that fold into the previous entry when they repeat on the same element: typing. */
const FOLDING_EVENTS: ReadonlySet<UiActivity['event']> = new Set(['input', 'keydown']);

/** Whether `entry` continues the run `last` stands for. */
function continuesRun(last: ActivityEntry | undefined, entry: UiActivity): last is UiActivity {
  return (
    last?.kind === 'ui' &&
    FOLDING_EVENTS.has(entry.event) &&
    last.event === entry.event &&
    last.target === entry.target &&
    last.key === entry.key &&
    last.scope === entry.scope &&
    last.instanceId === entry.instanceId
  );
}

/**
 * The shell's memory of recent activity: a ring of the newest entries, where a run of
 * typing on one element is one entry with a count rather than a page of them.
 */
export function createActivityLog({
  capacity = DEFAULT_CAPACITY,
  maxAgeMs = DEFAULT_MAX_AGE_MS
}: ActivityLogOptions = {}): ActivityLog {
  const ring = new RingBuffer<ActivityEntry>(capacity);
  return {
    record(entry) {
      if (entry.kind === 'ui') {
        const last = ring.last();
        if (continuesRun(last, entry)) {
          ring.replaceLast({ ...last, time: entry.time, count: last.count + entry.count });
          return;
        }
      }
      ring.push(entry);
    },
    recent(now = Date.now()) {
      return ring.toArray().filter((entry) => now - entry.time <= maxAgeMs);
    },
    clear() {
      ring.clear();
    }
  };
}
